/** An invite as `GET /api/invites/:id` returns it (packages/api domains/workspaces). */
export type InviteInfo = {
  id: string;
  workspaceId: string;
  workspace: string;
  inviter: string | null;
  email: string;
  role: string;
  status: "pending" | "accepted" | "expired";
  member: boolean;
};

/**
 * What the invite page does, given the invite and who is signed in:
 * - `open`: join (when `accept`) or just switch to the workspace, then open it
 * - `sign-in`: signed out, continue with the invited address
 * - `wrong-account`: signed in as someone else, offer to switch
 * - `used`: accepted already, and not by whoever is signed in
 * - `expired`
 */
export type InvitePlan =
  | { kind: "loading" }
  | { kind: "open"; accept: boolean }
  | { kind: "sign-in" }
  | { kind: "wrong-account" }
  | { kind: "used" }
  | { kind: "expired" };

export function invitePlan(
  invite: InviteInfo | null,
  signedInEmail: string | null,
  sessionPending: boolean,
): InvitePlan {
  if (!invite || sessionPending) return { kind: "loading" };
  const matches = signedInEmail?.toLowerCase() === invite.email.toLowerCase();
  // The invited account, already in: open it, using up a live invite (it may upgrade the role).
  // Another account that happens to be in the workspace (often whoever sent the invite) is still
  // the wrong account for this link: like maquette, offer to switch, never open it silently.
  if (matches && invite.member) return { kind: "open", accept: invite.status === "pending" };
  if (invite.status === "expired") return { kind: "expired" };
  if (invite.status === "accepted") return { kind: signedInEmail ? "used" : "sign-in" };
  if (!signedInEmail) return { kind: "sign-in" };
  return matches ? { kind: "open", accept: true } : { kind: "wrong-account" };
}

/** The sign-in page, with the invited address filled in, coming back to the invite. */
export function signInPath(inviteId: string, email: string) {
  const params = new URLSearchParams({ invite: inviteId });
  if (email) params.set("email", email);
  return `/auth/signin?${params}`;
}
